import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import logo from "../traderhub24-logo.svg";
import Footer from "./components/Footer";
import { useAuth } from './contexts/AuthContext';
import { supabase } from './lib/supabase';

interface PricingProps {
  onNavigate?: (page: "home" | "pricing" | "changelog" | "privacy-policy" | "terms-and-conditions" | "risk-warning" | "documentation" | "contact") => void;
  onRegister?: () => void;
  onLogin?: () => void;
}

function Pricing({ onNavigate, onRegister, onLogin }: PricingProps) {
  const { t } = useTranslation(['common', 'pages']);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const { user, session, membershipType, refreshProfile } = useAuth();
  const isLoggedIn = useMemo(() => Boolean(user), [user]);
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [stripeMessage, setStripeMessage] = useState<{ type: 'success' | 'cancel' | 'error'; text: string } | null>(null);

  const pricingReturnUrl = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    // App uses in-app pages; deep-link via ?page=pricing so Stripe return lands back on Pricing UI.
    return `${window.location.origin}/?page=pricing`;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const stripe = url.searchParams.get('stripe');
    if (!stripe) return;

    if (stripe === 'success') {
      setStripeMessage({ type: 'success', text: 'Payment successful. Your membership will update shortly.' });
      // Webhook updates profile; refresh so UI picks it up when ready.
      // (No aggressive polling; just a couple quick refreshes.)
      refreshProfile?.();
      setTimeout(() => refreshProfile?.(), 1500);
    } else if (stripe === 'cancel') {
      setStripeMessage({ type: 'cancel', text: 'Payment cancelled.' });
    }

    url.searchParams.delete('stripe');
    window.history.replaceState({}, document.title, url.toString());
  }, [refreshProfile]);

  const openBillingPortal = async () => {
    if (!isLoggedIn) {
      onLogin?.();
      return;
    }
    if (!session?.access_token) {
      onLogin?.();
      return;
    }
    if (!pricingReturnUrl) return;

    setPortalLoading(true);
    setStripeMessage(null);
    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: { returnUrl: pricingReturnUrl },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw error;
      if (!data?.url) throw new Error('Missing portal URL');
      window.location.assign(data.url);
    } catch (e: any) {
      setStripeMessage({ type: 'error', text: e?.message || 'Failed to open billing portal.' });
    } finally {
      setPortalLoading(false);
    }
  };

  const startCheckout = async (planId: 'pro' | 'publisher') => {
    if (!isLoggedIn) {
      onRegister?.();
      return;
    }
    if (!session?.access_token) {
      onLogin?.();
      return;
    }
    if (!pricingReturnUrl) return;

    setStripeMessage(null);
    setCheckoutLoadingPlan(planId);
    try {
      const interval = billingPeriod === 'yearly' ? 'year' : 'month';
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { returnUrl: pricingReturnUrl, interval, planId },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw error;
      if (!data?.url) throw new Error('Missing checkout URL');
      window.location.assign(data.url);
    } catch (e: any) {
      try {
        const ctx = e?.context;
        if (ctx && typeof ctx.text === 'function') {
          const bodyText = await ctx.text();
          setStripeMessage({ type: 'error', text: bodyText || e?.message || 'Failed to start checkout.' });
        } else {
          setStripeMessage({ type: 'error', text: e?.message || 'Failed to start checkout.' });
        }
      } catch {
        setStripeMessage({ type: 'error', text: e?.message || 'Failed to start checkout.' });
      }
      setCheckoutLoadingPlan(null);
    }
  };

  // Base monthly prices (before 50% discount)
  const basePrices = {
    free: 0,
    pro: 11.98, // Current $5.99 is 50% of this
    publisher: 19.98, // Current $9.99 is 50% of this
  };

  // Calculate prices with 50% discount applied
  const calculatePrice = (planId: 'free' | 'pro' | 'publisher', period: 'monthly' | 'yearly'): { price: number; originalPrice: number; periodText: string } => {
    const basePrice = basePrices[planId];
    
    if (planId === 'free') {
      return { price: 0, originalPrice: 0, periodText: period === 'monthly' ? t('pages:pricing.periodMonthly') : t('pages:pricing.periodYearly') };
    }

    if (period === 'monthly') {
      // Monthly: apply 50% discount
      const discountedPrice = basePrice * 0.5;
      return { 
        price: discountedPrice, 
        originalPrice: basePrice, 
        periodText: t('pages:pricing.periodMonthly')
      };
    } else {
      // Yearly: apply 20% discount first, then 50% discount
      const yearlyBase = basePrice * 12 * 0.8; // 20% discount
      const discountedPrice = yearlyBase * 0.5; // 50% discount on top
      const originalYearly = basePrice * 12 * 0.8; // Original yearly price
      return { 
        price: discountedPrice, 
        originalPrice: originalYearly, 
        periodText: t('pages:pricing.periodYearly')
      };
    }
  };

  const formatPrice = (price: number): string => {
    return `$${price.toFixed(2)}`;
  };

  const plans = [
    {
      id: 'free',
      title: t('pages:pricing.free.title'),
      price: t('pages:pricing.free.price'),
      period: t('pages:pricing.free.period'),
      description: t('pages:pricing.free.description'),
      features: [
        { label: t('pages:pricing.free.features.tradeTracking'), included: true },
        { label: t('pages:pricing.free.features.analytics'), included: true },
        { label: t('pages:pricing.free.features.journal'), included: true },
        { label: t('pages:pricing.free.features.strategies'), included: true },
        { label: t('pages:pricing.free.features.publishing'), included: false },
      ],
      button: t('pages:pricing.free.button'),
      popular: false,
    },
    {
      id: 'pro',
      title: t('pages:pricing.pro.title'),
      price: t('pages:pricing.pro.price'),
      period: t('pages:pricing.pro.period'),
      description: t('pages:pricing.pro.description'),
      features: [
        { label: t('pages:pricing.pro.features.everythingFree'), included: true },
        { label: t('pages:pricing.pro.features.journal'), included: true },
        { label: t('pages:pricing.pro.features.unlimitedStrategies'), included: true },
        { label: t('pages:pricing.pro.features.publishing'), included: true },
        { label: t('pages:pricing.pro.features.advancedAnalytics'), included: true },
        { label: t('pages:pricing.pro.features.strategyManagement'), included: true },
      ],
      button: t('pages:pricing.pro.button'),
      popular: true,
    },
    {
      id: 'publisher',
      title: t('pages:pricing.publisher.title'),
      price: t('pages:pricing.publisher.price'),
      period: t('pages:pricing.publisher.period'),
      description: t('pages:pricing.publisher.description'),
      features: [
        { label: t('pages:pricing.publisher.features.everythingPro'), included: true },
        { label: t('pages:pricing.publisher.features.advancedAnalytics'), included: true },
        { label: t('pages:pricing.publisher.features.customizedForSocialTraders'), included: true },
      ],
      button: t('pages:pricing.publisher.button'),
      popular: false,
    },
  ];

  const comparisonFeatures = [
    { key: 'trade', label: t('pages:pricing.comparison.features.trade') },
    { key: 'openTrade', label: t('pages:pricing.comparison.features.openTrade') },
    { key: 'advancedAnalytics', label: t('pages:pricing.comparison.features.advancedAnalytics') },
    { key: 'tradeJournal', label: t('pages:pricing.comparison.features.tradeJournal') },
    { key: 'strategies', label: t('pages:pricing.comparison.features.strategies') },
    { key: 'tradePublishing', label: t('pages:pricing.comparison.features.tradePublishing') },
  ];

  const getFeatureStatus = (planId: string, featureKey: string): string | boolean | 'limited' => {
    // Return limit text or boolean/limited status
    // For now, return empty string - limits will be added later
    if (planId === 'free') {
      switch (featureKey) {
        case 'trade':
          return '100/year'; // Example limit
        case 'openTrade':
          return '10';
        case 'advancedAnalytics':
          return false;
        case 'tradeJournal':
          return 'Limited';
        case 'strategies':
          return '3';
        case 'tradePublishing':
          return false;
        default:
          return false;
      }
    } else if (planId === 'pro') {
      switch (featureKey) {
        case 'trade':
          return '1000/year';
        case 'openTrade':
          return '50';
        case 'advancedAnalytics':
          return true;
        case 'tradeJournal':
          return 'Unlimited';
        case 'strategies':
          return '50';
        case 'tradePublishing':
          return true;
        default:
          return false;
      }
    } else if (planId === 'publisher') {
      switch (featureKey) {
        case 'trade':
          return 'Unlimited';
        case 'openTrade':
          return 'Unlimited';
        case 'advancedAnalytics':
          return 'Advanced';
        case 'tradeJournal':
          return 'Unlimited';
        case 'strategies':
          return 'Unlimited';
        case 'tradePublishing':
          return 'Unlimited';
        default:
          return false;
      }
    }
    return false;
  };

  return (
    <div className="min-h-screen bg-app-box text-gray-900">
      {/* Header */}
      <header className="w-full py-3 md:py-4 px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <img 
            src={logo} 
            alt="TraderHub24" 
            className="h-10 md:h-14 w-auto cursor-pointer hover:opacity-80 transition-opacity" 
            onClick={() => onNavigate && onNavigate("home")}
          />
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <a 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (onNavigate) onNavigate("home");
              }}
              className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors"
            >
              {t('common:nav.home')}
            </a>
            <a 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (onNavigate) onNavigate("documentation");
              }}
              className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors"
            >
              {t('common:footer.documentation')}
            </a>
            <button
              onClick={onLogin}
              className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors"
            >
              {t('common:header.signIn')}
            </button>
            <button
              onClick={onRegister}
              className="px-6 py-2.5 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors shadow-sm"
            >
              {t('pages:landing.getStarted')}
            </button>
          </nav>
          {/* Mobile Menu Button */}
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="md:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {showMobileMenu ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
        {/* Mobile Menu */}
        {showMobileMenu && (
          <nav className="md:hidden mt-4 pb-4 border-b border-gray-200 animate-in slide-in-from-top duration-200">
            <div className="flex flex-col gap-2">
              <a 
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowMobileMenu(false);
                  if (onNavigate) onNavigate("home");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {t('common:nav.home')}
              </a>
              <a 
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowMobileMenu(false);
                  if (onNavigate) onNavigate("documentation");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {t('common:footer.documentation')}
              </a>
              <button
                onClick={() => {
                  setShowMobileMenu(false);
                  if (onLogin) onLogin();
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-left"
              >
                {t('common:header.signIn')}
              </button>
              <button
                onClick={() => {
                  setShowMobileMenu(false);
                  if (onRegister) onRegister();
                }}
                className="px-4 py-2 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors text-sm mt-2"
              >
                {t('pages:landing.getStarted')}
              </button>
            </div>
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12 lg:py-20">
        {stripeMessage && (
          <div
            className={`mb-6 md:mb-8 rounded-lg border px-4 py-3 text-sm ${
              stripeMessage.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-800'
                : stripeMessage.type === 'cancel'
                  ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
                  : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {stripeMessage.text}
          </div>
        )}

        {(isLoggedIn && membershipType !== 'free') && (
          <div className="mb-8 flex justify-center">
            <button
              onClick={openBillingPortal}
              disabled={portalLoading}
              className="px-6 py-2.5 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {portalLoading ? 'Opening…' : t('pages:account.manageBilling')}
            </button>
          </div>
        )}

        {/* Header Section */}
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-black mb-4">
            {t('pages:pricing.title')}
          </h1>
          <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto mb-6">
            {t('pages:pricing.subtitle')}
          </p>
          
          {/* Limited Time Discount Badge */}
          <div className="inline-flex items-center gap-2 bg-red-100 border-2 border-red-500 text-red-700 px-4 md:px-6 py-2 md:py-3 rounded-lg mb-8 animate-pulse">
            <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm md:text-base font-bold">{t('pages:pricing.limitedTimeDiscount')}</span>
          </div>

          {/* Billing Period Toggle */}
          <div className="flex flex-col items-center justify-center gap-2 mb-8">
            <div className="flex items-center justify-center gap-4 relative">
              <span className={`text-sm md:text-base font-medium transition-colors ${billingPeriod === 'monthly' ? 'text-black' : 'text-gray-500'}`}>
                {t('pages:pricing.monthly')}
              </span>
              <button
                onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
                className={`relative w-14 h-8 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                  billingPeriod === 'yearly' ? 'bg-orange-500' : 'bg-gray-200'
                }`}
              >
                <div
                  className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
                    billingPeriod === 'yearly' ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className={`text-sm md:text-base font-medium transition-colors ${billingPeriod === 'yearly' ? 'text-black' : 'text-gray-500'}`}>
                {t('pages:pricing.yearly')}
              </span>
              {billingPeriod === 'yearly' && (
                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-green-500 text-white text-xs font-semibold px-2 py-1 rounded whitespace-nowrap z-10">
                  {t('pages:pricing.savings')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`bg-app-box border rounded-2xl p-5 md:p-6 lg:p-8 shadow-sm relative ${
                plan.popular
                  ? 'border-2 border-orange-500 bg-orange-50'
                  : 'border-gray-200'
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 bg-orange-500 text-white px-3 md:px-4 py-1 rounded-bl-lg rounded-tr-2xl text-[10px] md:text-xs lg:text-sm font-semibold">
                  {t('pages:pricing.pro.popular')}
                </div>
              )}
              <div className="text-center mb-5 md:mb-6">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">{plan.title}</h3>
                <div className="mb-3 md:mb-4">
                  {plan.id !== 'free' ? (
                    <>
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <span className="text-3xl md:text-4xl font-bold text-gray-900">
                          {formatPrice(calculatePrice(plan.id as 'free' | 'pro' | 'publisher', billingPeriod).price)}
                        </span>
                        <span className="text-gray-600 text-sm md:text-base">
                          {calculatePrice(plan.id as 'free' | 'pro' | 'publisher', billingPeriod).periodText}
                        </span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-sm text-gray-400 line-through">
                          {formatPrice(calculatePrice(plan.id as 'free' | 'pro' | 'publisher', billingPeriod).originalPrice)}
                        </span>
                        <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded">
                          {t('pages:pricing.discount')}
                        </span>
                      </div>
                      {billingPeriod === 'yearly' && (
                        <div className="mt-1">
                          <span className="text-xs text-green-600 font-semibold">
                            {t('pages:pricing.monthlyEquivalent')} {formatPrice(calculatePrice(plan.id as 'free' | 'pro' | 'publisher', billingPeriod).price / 12)}{t('pages:pricing.periodMonthly')}
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-3xl md:text-4xl font-bold text-gray-900">$0</span>
                      <span className="text-gray-600 text-sm md:text-base">{billingPeriod === 'monthly' ? t('pages:pricing.periodMonthly') : t('pages:pricing.periodYearly')}</span>
                    </div>
                  )}
                </div>
                <p className="text-xs md:text-sm text-gray-600">{plan.description}</p>
              </div>
              <ul className="space-y-2.5 md:space-y-3 mb-6 md:mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2">
                    {feature.included ? (
                      <svg className="w-4 h-4 md:w-5 md:h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className={`text-xs md:text-sm lg:text-base ${feature.included ? "text-gray-700" : "text-gray-500"}`}>
                      {feature.label}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => {
                  if (!isLoggedIn) return onRegister?.();
                  if (plan.id === 'free') return;

                  // If user already has an active membership, manage/switch via Stripe portal to avoid double subscriptions.
                  if (membershipType !== 'free') return openBillingPortal();

                  if (plan.id === 'pro') return startCheckout('pro');
                  if (plan.id === 'publisher') return startCheckout('publisher');
                }}
                disabled={
                  (isLoggedIn && plan.id === membershipType) ||
                  checkoutLoadingPlan === plan.id ||
                  portalLoading
                }
                className={`w-full px-4 md:px-6 py-2.5 md:py-3 font-semibold rounded-lg transition-colors text-sm md:text-base ${
                  plan.popular
                    ? 'bg-orange-500 text-white hover:bg-orange-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                }`}
              >
                {isLoggedIn && plan.id === membershipType
                  ? t('pages:account.plan')
                  : checkoutLoadingPlan === plan.id
                    ? 'Redirecting…'
                    : plan.button}
              </button>
            </div>
          ))}
        </div>

        {/* Comparison Table */}
        <div className="mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8 text-center">
            {t('pages:pricing.comparison.title')}
          </h2>
          <div className="bg-app-box border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 md:px-6 py-3 md:py-4 text-left text-sm font-semibold text-gray-900">
                      {t('pages:pricing.comparison.featuresHeader')}
                    </th>
                    <th className="px-4 md:px-6 py-3 md:py-4 text-center text-sm font-semibold text-gray-900">
                      {t('pages:pricing.free.title')}
                    </th>
                    <th className="px-4 md:px-6 py-3 md:py-4 text-center text-sm font-semibold text-gray-900">
                      {t('pages:pricing.pro.title')}
                    </th>
                    <th className="px-4 md:px-6 py-3 md:py-4 text-center text-sm font-semibold text-gray-900">
                      {t('pages:pricing.publisher.title')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {comparisonFeatures.map((feature, index) => {
                    const freeStatus = getFeatureStatus('free', feature.key);
                    const proStatus = getFeatureStatus('pro', feature.key);
                    const publisherStatus = getFeatureStatus('publisher', feature.key);

                    const renderCell = (status: string | boolean | 'limited') => {
                      if (status === 'limited') {
                        return (
                          <span className="text-xs text-gray-500">{t('common:limited')}</span>
                        );
                      } else if (typeof status === 'string') {
                        // Show limit text
                        return (
                          <span className="text-sm font-medium text-gray-700">{status}</span>
                        );
                      } else if (status === true) {
                        return (
                          <svg className="w-5 h-5 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        );
                      } else {
                        return (
                          <svg className="w-5 h-5 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        );
                      }
                    };

                    return (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 md:px-6 py-3 md:py-4 text-sm font-medium text-gray-900">
                          {feature.label}
                        </td>
                        <td className="px-4 md:px-6 py-3 md:py-4 text-center">
                          {renderCell(freeStatus)}
                        </td>
                        <td className="px-4 md:px-6 py-3 md:py-4 text-center">
                          {renderCell(proStatus)}
                        </td>
                        <td className="px-4 md:px-6 py-3 md:py-4 text-center">
                          {renderCell(publisherStatus)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer currentPage="pricing" onNavigate={onNavigate} />
    </div>
  );
}

export default Pricing;

